import { StudentStats } from "./reports-model"
import { LessonSearchInfo, SchoolClassSummary, Report } from "./tables"

export interface ErrorResult {
  statusCode: number
  message: string
  body?: any | null
}

export interface ImageResizeResponse {
  questionImageUrl: string
  questionImageWidth: number
  questionImageHeight: number
  questionDesktopImageUrl: string
}

export type ImageResizeResult = {
  url: string
  size: { width: number; height: number }
}

export type ImageUploadResult = {
  web: ImageResizeResult
  desktop: ImageResizeResult
}

export type AudioUploadResult = {
  url: string
}

export const DEFAULT_PROFILE_PICTURE = "/img/user.jpg"

// LoggedInUserResponse
/*
* public class LoggedInUserResponse
    {
        public Guid Id { get; set; }
        public string Name { get; set; }
        public string Phone { get; set; }
        public string SchoolName { get; set; }
        public string City { get; set; }
        public string Email { get; set; }
        public int LessonsCount { get; set; }
        public string AvatarUrl { get; set; }
        public Guid? SchoolId  { get; set; }
        public string LanguageCode { get; set; }
    }
* */
export interface LoggedInUser {
  id: string
  avatarId?: string
  name: string
  phone?: string
  city?: string
  schoolName?: string
  email?: string
  /** Set: mail to the address fails at the sender (registered before the address check) */
  emailUndeliverable?: boolean
  lessonsCount: number
  schoolId?: string
  languageCode?: string
  profilePictureUrl: string
  isTeacher?: boolean
  isStudent?: boolean
  canUploadEnvles?: boolean
  canCreateCertificateLinks?: boolean
  canViewVitosha?: boolean
  canPurchaseWithCard?: boolean
  isBusinessUser?: boolean
  logoUrl?: string
}

export interface MonSchoolSelectResponse {
  id: string
  name: string
}

export interface NvoStatsForClass {
  className: string
  playedGames: number
  successRate: number
  studentStats: NvoStudentStats[]
}

export interface NvoStudentStats {
  studentName: string
  playedGames: number
  successRate: number
  statsByGame: NvoStatsByGame[]
}

export interface NvoStatsByGame {
  gamecode: string
  correctAsnwers: number
  incorrectAsnwers: number
  successRate: number
  themes: NvoStatsByTheme[]
}

export interface NvoStatsByTheme {
  name: string
  correctAsnwers: number
  incorrectAsnwers: number
  successRate: number
}

export interface Page<T> {
  pageSize: number
  totalCountPages: number
  totalItemsCount: number
  items: T[]
}

export default interface ProblemDetails {
  type: string
  title: string
  status: number
  detail: string
  instance: string
}

export function asProblemDetails(error: any): ProblemDetails | null {
  if (error === null) return null
  if (typeof error !== "object") return null

  if (typeof error.type !== "string") return null
  if (typeof error.title !== "string") return null
  if (typeof error.status !== "number") return null
  if (typeof error.detail !== "string") return null
  if (typeof error.instance !== "string") return null

  return error
}

export interface ValidationProblemDetailsErrors {
  [fieldName: string]: string[]
}

export interface ValidationProblemDetails extends ProblemDetails {
  errors: ValidationProblemDetailsErrors
}

export function asValidationProblemDetails(
  error: any
): ValidationProblemDetails | null {
  const errorDetails = asProblemDetails(error)
  if (errorDetails === null) return null
  if (typeof (errorDetails as any).errors !== "object") return null
  if ((errorDetails as any).errors === null) return null

  return errorDetails as ValidationProblemDetails
}

export function asValidationProblemDetailsErrors(
  error: any
): ValidationProblemDetailsErrors {
  if (error === null) return {}
  if (typeof error !== "object") return {}
  if (typeof error.errors !== "object") return {}
  return error.errors
}

export function fieldErrorFromProblemDetails(
  details: ValidationProblemDetails | null,
  fieldName: string,
  fallback: string
): string[] {
  if (details === null) {
    return [fallback]
  }

  let errorMessages = details.errors[fieldName]
  if (Array.isArray(errorMessages)) return errorMessages

  if (fieldName.length > 1) {
    const altFieldName =
      fieldName.charAt(0).toUpperCase() + fieldName.substring(1)
    errorMessages = details.errors[altFieldName]

    if (Array.isArray(errorMessages)) return errorMessages
  }

  return [fallback]
}

export interface SchoolAllClasses {
  myClasses: SchoolClassSummary[]
  otherClasses: SchoolClassSummary[]
}

export default interface StudentClassResponse {
  classId: string
  name: string
  invitationCode: string
}

export interface ClassStudentResponse {
  userId: string
  fullName: string
  emailOrUsername: string
  emailOrUsernameIsAutoGenerated?: boolean
  parentEmail?: string
  avatar?: number
}

export type Student = ClassStudentResponse

/** One hit of the class-list search: a class whose name matches and/or that has matching students (only those are listed). */
export interface ClassSearchResponse {
  id: string
  name: string
  countStudents: number
  nameMatches: boolean
  students: ClassStudentResponse[]
}

export interface StudentsNotInClass {
  className: string
  studentsInClass: Student[]
  studentsInSchool: Student[]
}

export interface UserProfile {
  id: string
  name: string
  country: string
  city: string
  userId: string
}

export interface ClassResponse {
  id: string
  name: string
  registraionCode: string
  schoolId: string
  maxStudents: number
  students: ClassStudentResponse[]
  countStudents: number
}

export interface DashboardLesson {
  key: string
  id: string
  title?: string
  imageUrl?: string
  countScreens: number
  countTimesPlayed: number
}

export interface DashboardReport {
  key: string
  reportId: string
  lessonId?: string
  title: string
  imageUrl?: string
  date: string
  gameCode?: string
  successRate: number
}

export interface DashboardLeaderboardSchool {
  id: string
  name: string
  city: string
  region: string
  count: number
}

export interface DashboardBlogPost {
  key: string
  title: string
  datePublished: string
  link?: string
}

export interface CountName {
  count: number
  name: string
}

export interface DashboardContents {
  latestLessons: DashboardLesson[]
  latestReports: DashboardReport[]
  leaderboard: DashboardLeaderboardSchool[]
  region?: string
  schoolId?: string
  countCreatedLessons: number
  countPlayedLessons: number
  countCopiedLessons: number
  countReportedAnswers: number
}

export interface DiscoverPageResult {
  promotedLessons: Pagination<LessonSearchInfo>
  latestLessons: Pagination<LessonSearchInfo>
  categories: string[]
}

export interface Pagination<T> {
  items: T[]
  pageSize: number
  next: string
}

export enum AiAudience {
  PrimarySchool,
  MiddleSchool,
  HighSchool,
  Tertiary,
}

export interface KhanAcademyVideo {
  id: number
  course: string
  grade?: string | null
  subject?: string | null
  khanAcademyUrl?: string | null
  ietfLanguageTag: string
  title: string
  description?: string | null
  youtubeId: string
}

export interface SimplePage<T> {
  items: T[]
  pageSize: number
}

export interface StudentReportRequest {
  studentId: string
}

export interface StudentReportResponse {}

export interface UserReportsPage extends Page<Report> {
  id: string
  name: string
  className: string
}

export interface StudentReport {
  studentName: string
  className: string
  report: Report
  stats: StudentStats
}
